import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { transactionAPI, fieldSettingAPI } from '../services/api';
import { FIELD_LABEL_DEFAULTS } from '../utils/fieldLabelDefaults';

const actionLabels = {
    'create': '계약 생성',
    'update': '계약 수정',
    'contract:create': '계약 생성',
    'contract:update': '계약 수정',
    'contract:delete': '계약 삭제',
    'contract:child-deleted': '하위 계약 삭제',
    'contract:additional-burial-created': '안치자 추가 계약 생성',
    'additional-burial:add': '안치자 추가',
    'cost:add': '추가 비용 등록',
    'cost:update': '비용 수정',
    'cost:status': '비용 상태 변경',
    'cost:delete': '비용 삭제',
    'cost:basic-status': '기본 비용 납부 상태 변경',
    'cost:stone-status': '석물 비용 납부 상태 변경',
    'cost:engraving-status': '각자비 납부 상태 변경',
    'cost:cost-item-status': '비용 항목 납부 상태 변경',
    'additional-cost:add': '추가 비용 등록',
    'additional-cost:status': '추가 비용 상태 변경',
    'additional-cost:delete': '추가 비용 삭제',
    'basic-cost:update': '기본 비용 상태 변경',
    'stone:update': '석물 정보 수정',
    'stone:status': '석물 상태 변경',
    'engraving:update': '각자 정보 수정',
    'management-fee:pay': '관리비 납부',
    'management-fee:create': '관리비 등록',
    'management-fee:update': '관리비 수정',
    'management-fee:update-status': '관리비 상태 변경',
    'management-fee:delete': '관리비 삭제',
    'maintenance:add': '보수 등록',
    'maintenance:status': '보수 상태 변경',
    'maintenance:update': '보수 수정',
    'maintenance:delete': '보수 삭제',
    'memo:add': '메모 등록',
    'memo:update': '메모 수정',
    'memo:delete': '메모 삭제',
};

const DETAIL_KEY_FALLBACK_LABEL = {
    fee_id: '관리비 ID',
    stone_id: '석물 ID',
    engraving_id: '각자 ID',
    maintenance_id: '보수·민원 ID',
    cost_item_id: '비용 항목 ID',
    cost_id: '추가 비용 ID',
    id: 'ID',
    deleted_child_transaction_id: '삭제된 하위 계약 ID',
    cemetery_id: '묘지 ID',
    at: '시각',
    cost_type: '비용 구분',
    cost_name: '항목명',
    period_start: '관리기간 시작',
    period_end: '관리기간 종료',
    fee_period_start: '관리기간 시작',
    fee_period_end: '관리기간 종료',
    period: '부과 기간',
    due_date: '납부기한',
    description: '설명',
    status: '상태',
    maintenance_type: '보수 구분',
    maintenance_date: '보수일',
    applicant_name: '신청인',
    applicant_phone: '연락처',
    manager: '담당자',
    reception_type: '접수 유형',
    cost: '비용',
    payment_method: '납부 방법',
    payment_date: '납부일',
    amount: '금액',
    category: '구분',
    content: '내용',
    is_important: '중요',
    updated_by: '수정자',
};

const COST_TYPE_LABEL_KEYS = [
    'usage_fee',
    'management_fee',
    'burial_work_fee',
    'installation_fee',
    'repair_fee',
    'removal_work_fee',
    'other_work_fee',
    'stone_processing_fee',
    'agency_fee',
    'reuse_fee',
];

function formatMaintenanceStatusValue(v) {
    const map = {
        pending: '대기',
        in_progress: '진행 중',
        completed: '완료',
        cancelled: '취소',
    };
    return map[v] || v;
}

function formatDetailValue(raw, key, action, getFieldLabel) {
    if (raw === null || raw === undefined) return '(없음)';
    if (typeof raw === 'boolean') return raw ? '예' : '아니오';
    if (typeof raw === 'object') {
        try {
            return JSON.stringify(raw);
        } catch {
            return String(raw);
        }
    }
    if (key === 'status' && String(action || '').startsWith('maintenance')) {
        return formatMaintenanceStatusValue(String(raw));
    }
    if (key === 'cost_type' && COST_TYPE_LABEL_KEYS.includes(String(raw))) {
        const code = String(raw);
        return getFieldLabel(code, FIELD_LABEL_DEFAULTS[code] || code);
    }
    if ((key === 'amount' || key === 'cost' || key === 'total_cost') && (typeof raw === 'number' || /^-?\d/.test(String(raw)))) {
        const n = Number(raw);
        if (!Number.isNaN(n)) return n.toLocaleString('ko-KR');
    }
    if (key === 'payment_date' || key === 'completed_at' || key === 'due_date' || key === 'at') {
        try {
            const d = new Date(raw);
            if (!Number.isNaN(d.getTime())) return d.toLocaleString('ko-KR');
        } catch {
            /* fallthrough */
        }
    }
    return String(raw);
}

function resolveDetailKeyLabel(key, getFieldLabel) {
    if (FIELD_LABEL_DEFAULTS[key]) {
        return getFieldLabel(key, FIELD_LABEL_DEFAULTS[key]);
    }
    if (DETAIL_KEY_FALLBACK_LABEL[key]) {
        return DETAIL_KEY_FALLBACK_LABEL[key];
    }
    return key;
}

/** 계약 수정(changes/sections)이 아닌 일반 이력 JSON → 읽기 쉬운 줄 목록 */
function formatGenericHistoryDetails(obj, action, getFieldLabel) {
    const skip = new Set(['changes', 'sections', 'fields']);
    const keys = Object.keys(obj || {}).filter((k) => !skip.has(k));
    if (keys.length === 0) return [];

    const lines = [];
    for (const key of keys) {
        const v = obj[key];
        if (v === undefined) continue;
        const label = resolveDetailKeyLabel(key, getFieldLabel);
        const display = formatDetailValue(v, key, action, getFieldLabel);
        lines.push(`${label}: ${display}`);
    }
    return lines;
}

const HISTORY_AREA_LABELS = {
    transaction: '계약',
    mausoleum: '묘소',
    cost: '비용',
    acquaintances: '연고자',
    dead: '사망자',
    contract_application_document: '계약 신청 서류',
    stone_object: '석물',
    stone_objects: '석물',
    engraving: '각자',
    documents: '서류',
};

function formatHistoryArea(area) {
    if (!area) return '';
    const deadM = /^dead\[(\d+)\]$/.exec(area);
    if (deadM) return `사망자 ${deadM[1]}`;
    const acqM = /^acquaintance\[(\d+)\]$/.exec(area);
    if (acqM) return `연고자 ${acqM[1]}`;
    const engM = /^engraving\[(\d+)\]$/.exec(area);
    if (engM) return `각자 ${engM[1]}`;
    return HISTORY_AREA_LABELS[area] || area;
}

/** field_definitions.field_code 로 매핑 (묘지 커스텀 라벨과 동일 키 사용) */
function resolveFieldDefinitionCode(area, field) {
    if (!field || field === '(신규)' || field === '(삭제)') return field;
    if (/^dead\[\d+\]$/.test(area)) {
        const map = {
            name: 'dead_name',
            resident_number: 'dead_resident_number',
            address: 'dead_address',
            address_detail: 'dead_address_detail',
        };
        return map[field] || field;
    }
    if (/^acquaintance\[\d+\]$/.test(area)) {
        const map = {
            name: 'acquaintance_name',
            resident_number: 'acquaintance_resident_number',
            road_address: 'acquaintance_address',
            address_detail: 'acquaintance_address_detail',
        };
        return map[field] || field;
    }
    if (area === 'mausoleum' && field === 'etc') return 'mausoleum_etc';
    if (area === 'cost' && field === 'memo') return 'cost_memo';
    if (/^engraving\[\d+\]$/.test(area)) {
        if (field === 'total_cost') return 'engraving_fee';
        if (field === 'memo') return 'engraving_memo';
        if (field === 'engraving_type') return 'engraving_type';
        return field;
    }
    return field;
}

function historyFieldLabel(area, field, getFieldLabel) {
    if (!field) return '';
    if (field === '(신규)' || field === '(삭제)') return field;
    const code = resolveFieldDefinitionCode(area, field);
    const defaultLabel =
        FIELD_LABEL_DEFAULTS[code] ?? FIELD_LABEL_DEFAULTS[field] ?? field;
    return getFieldLabel(code, defaultLabel);
}

function formatHistoryDetails(obj, getFieldLabel, action) {
    if (obj == null) return [];
    if (typeof obj !== 'object') return [String(obj)];

    const changes = Array.isArray(obj.changes) ? obj.changes : [];
    if (changes.length > 0) {
        return changes
            .filter((c) => !(c.area === 'cost' && c.field === 'total_cost'))
            .map((c) => {
            const area = formatHistoryArea(c.area);
            const label = historyFieldLabel(c.area, c.field, getFieldLabel);
            const head = area ? `${area} · ${label}` : label;
            return `${head}: ${c.from} -> ${c.to}`;
        });
    }

    const lines = [];
    if (Array.isArray(obj.sections)) {
        for (const s of obj.sections) {
            if (!s || typeof s !== 'object') continue;
            const a = formatHistoryArea(s.area) || s.area;
            if (s.area === 'transaction' && Array.isArray(s.fields) && s.fields.length) {
                const labels = s.fields.map((f) =>
                    historyFieldLabel('transaction', f, getFieldLabel)
                );
                lines.push(`${a}: ${labels.join(', ')}`);
            } else if (s.count != null) {
                lines.push(`${a} (${s.count}건)`);
            } else if (Array.isArray(s.keys) && s.keys.length) {
                const labels = s.keys.map((k) =>
                    historyFieldLabel(s.area || 'documents', k, getFieldLabel)
                );
                lines.push(`${a}: ${labels.join(', ')}`);
            } else {
                lines.push(a);
            }
        }
    }
    if (obj.updated_at) {
        try {
            lines.push(`처리 시각: ${new Date(obj.updated_at).toLocaleString()}`);
        } catch {
            lines.push(`처리 시각: ${obj.updated_at}`);
        }
    }
    if (lines.length > 0) return lines;

    const genericLines = formatGenericHistoryDetails(obj, action, getFieldLabel);
    if (genericLines.length > 0) return genericLines;

    try {
        return [JSON.stringify(obj)];
    } catch {
        return [String(obj)];
    }
}

const ContractHistory = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { data: contract } = useQuery({
        queryKey: ['contract', id],
        queryFn: () => transactionAPI.getById(id),
        select: (res) => res.data,
    });

    const cemeteryId = contract?.cemetery_id ?? contract?.cemeteryId;

    const { data: fieldSettings } = useQuery({
        queryKey: ['fieldSettings', cemeteryId],
        queryFn: () => fieldSettingAPI.getFieldSettingsByCemetery(cemeteryId),
        select: (res) => res.data.data,
        enabled: !!cemeteryId,
    });

    const getFieldLabel = (fieldCode, defaultLabel) => {
        if (!fieldSettings || !fieldSettings.fields) return defaultLabel;
        const field = fieldSettings.fields.find((f) => f.field_code === fieldCode);
        return field?.custom_label || field?.label || defaultLabel;
    };

    const { data, isLoading, error } = useQuery({
        queryKey: ['contract-histories', id],
        queryFn: () => transactionAPI.getHistories(id),
        select: (res) => res.data,
    });

    if (isLoading) return <div className="p-6">수정 이력을 불러오는 중...</div>;
    if (error) return <div className="p-6 text-red-600">오류: {error.message}</div>;

    const histories = data?.histories || [];

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold">수정 이력</h1>
                <button className="btn btn-secondary" onClick={() => navigate(`/contracts/${id}`)}>돌아가기</button>
            </div>

            <div className="card">
                {histories.length === 0 ? (
                    <div className="text-gray-500">이력이 없습니다.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead>
                                <tr className="text-left text-gray-600">
                                    <th className="py-2 pr-4">일시</th>
                                    <th className="py-2 pr-4">사용자</th>
                                    <th className="py-2 pr-4">작업</th>
                                    <th className="py-2 pr-4">상세</th>
                                </tr>
                            </thead>
                            <tbody>
                                {histories.map((h) => {
                                    let detailLines = [];
                                    try {
                                        if (h.details) {
                                            const obj = typeof h.details === 'string' ? JSON.parse(h.details) : h.details;
                                            detailLines = formatHistoryDetails(obj, getFieldLabel, h.action);
                                        }
                                    } catch (_) {
                                        detailLines = [h.details];
                                    }
                                    return (
                                        <tr key={h.id} className="border-t">
                                            <td className="py-2 pr-4 whitespace-nowrap">{new Date(h.created_at || h.createdAt).toLocaleString()}</td>
                                            <td className="py-2 pr-4">{h.user?.name || h.user?.id || '-'}</td>
                                            <td className="py-2 pr-4">{actionLabels[h.action] || h.action}</td>
                                            <td className="py-2 pr-4 text-gray-700 break-words">
                                                {detailLines.length === 0 ? (
                                                    '-'
                                                ) : (
                                                    <ul className="list-disc pl-4 space-y-1 m-0">
                                                        {detailLines.map((line, i) => (
                                                            <li key={i}>{line}</li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ContractHistory;


